import { Component, ContentType, Project } from 'contensis-core-api';
import { Node } from 'contensis-delivery-api';
import { Entry, TagGroup } from 'contensis-management-api';
import { ContentTypeAssertions } from './ContentTypeAssertions';
import { ContentTypeFieldHelper } from './ContentTypeFieldHelper';
import { ContentTypeResolver } from './ContentTypeResolver';
import { ContentTypeTransformations } from './ContentTypeTransformations';
import * as EntryHelpers from '../EntryHelper';
import { hasKeys } from '../typeguards';
import { IContensisContext, Model, ModelDependencies } from '~/models';
import { EntryDependencyMeta } from '~/models/EntryDependencies';
import { FormContentType } from '~/models/FormContentTypeManagement';
/**
 * A collection of helper methods for resolving and extracting data from content types or components
 */
class ContentTypeHelper {
  static assert = ContentTypeAssertions;
  static entry = EntryHelpers;
  static field = ContentTypeFieldHelper;
  static transform = ContentTypeTransformations;
  static resolve = ContentTypeResolver;

  private getById = (contentTypeOrComponentId: string) =>
    this.dependencies.contentTypes.find(
      (d) => d.id === contentTypeOrComponentId
    ) ||
    this.dependencies.components.find((d) => d.id === contentTypeOrComponentId);

  private getByContentId = (entryOrNodeId: string) =>
    this.dependencies.defaults.find((d) => d.id === entryOrNodeId) ||
    this.dependencies.nodes.find((d) => d.id === entryOrNodeId);

  private getByTagGroupId = (tagGroupId: string) =>
    this.dependencies.tagGroups.find((d) => d.id === tagGroupId);

  assetTypes: ContentType[]; // Hold asset types to use when removing field validations in an asset field
  contentTypes: ContentType[];
  components: Component[];
  forms: FormContentType[];
  dependencies: {
    contentTypes: ModelDependencies[];
    components: ModelDependencies[];
    defaults: (Omit<ModelDependencies, 'dependencies'> & {
      dependencies: EntryDependencyMeta[];
    })[];
    nodes: ModelDependencies[];
    tagGroups: ModelDependencies[];
    getById: (
      contentTypeOrComponentId: string
    ) => ModelDependencies | undefined;
    getByContentId: (
      entryOrNodeId: string
    ) =>
      | ContentTypeHelper['dependencies']['defaults'][0]
      | ContentTypeHelper['dependencies']['nodes'][0]
      | undefined;
    getByTagGroupId: (tagGroupId: string) => ModelDependencies | undefined;
  } = {
    contentTypes: [],
    components: [],
    defaults: [],
    nodes: [],
    tagGroups: [],
    getById: this.getById,
    getByContentId: this.getByContentId,
    getByTagGroupId: this.getByTagGroupId,
  };
  /** Contextual entries resolved from content models */
  entries: Entry[] = [];
  /** Built content models */
  models: Model[] = [];
  /** Contextual nodes resolved from content models */
  nodes: Node[] = [];
  /** The Contensis project */
  project?: Project;
  resolve: ContentTypeResolver;
  /** Contextual tag groups resolved from content models */
  tagGroups: TagGroup[] = [];

  /** Content type and Form assertions */
  assert = ContentTypeAssertions;
  entry = EntryHelpers;
  /** Content type field assertions */
  field = ContentTypeFieldHelper;
  /** Transform field data within content types or components */
  transform = ContentTypeTransformations;

  /** Only works when the payload is available when used as IContensisContext */
  get isCopyOperation() {
    const payload = (this as unknown as IContensisContext).payload;
    return hasKeys(payload.copyField || payload.updateField);
  }

  constructor(
    contentTypes: ContentType[],
    components: Component[],
    tagGroups: TagGroup[],
    project?: Project
  ) {
    const { entryContentTypes, assetTypes, formContentTypes } =
      ContentTypeTransformations.SeparateContentTypesAndFilterComponentFields(
        contentTypes
      );
    this.assetTypes = assetTypes;
    this.contentTypes = [...entryContentTypes, ...formContentTypes];
    this.components = components;
    this.forms = formContentTypes;
    this.tagGroups = tagGroups;
    this.project = project;
    // Preload the resolve methods with the lists of resources
    this.resolve = new ContentTypeResolver(
      this.contentTypes, // use the `this` property so it shares the same reference
      this.components,
      this.tagGroups
    );
  }

  /**
   * Return a list of content type ids from this repository
   */
  getContentTypeIds = () => this.contentTypes?.map((c) => c.id) || [];
}

export default ContentTypeHelper;
